import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { formatMoney } from '../../mirror/time';
import { byRecency } from '../../money/recents';
import type { T, UiLocale } from '../../messages';
import type { ItemImage, Product, Service } from '../../wire';
import { ItemThumb } from '../ItemPhotos';
import {
  addOnSuggestions,
  groupServices,
  liveOptions,
  sellableProducts,
  sellableServices,
} from './model';
import type { LineDraft, ServiceOption } from './model';

/**
 * **What she sells** — the left half of the till (the top half on a phone).
 *
 * A search box, then Services / Products as two tabs (only the tabs that have
 * something in them), services under her own menu headings in her order, each
 * a tile with its name, price and length. Three rules that are not layout:
 *
 *  - **A service sold several ways never guesses.** Tapping it opens its
 *    lengths, each with its own price, and the line carries the chosen
 *    option's id so the server lists it at that option's price (0048).
 *  - **Attach-only add-ons are never sold standing alone.** They appear only as
 *    "Add to <her facial>" once the facial is in the cart, at the price they
 *    cost *attached*.
 *  - **Retired things never show** — an inactive item, an inactive length, an
 *    inactive link.
 *
 * Typing searches both tabs at once, with what this till rang most recently
 * first; Enter takes the single remaining match and does nothing when there are
 * two, because guessing which one she meant is how a till rings the wrong $95.
 */
export function Catalog({
  services,
  products,
  lines,
  recents,
  currency,
  locale,
  t,
  onAddService,
  onAddAddOn,
  onPickProduct,
}: {
  services: readonly Service[];
  products: readonly Product[];
  lines: readonly LineDraft[];
  recents: readonly string[];
  currency: string;
  locale: UiLocale;
  t: T;
  onAddService: (service: Service, option?: ServiceOption) => void;
  onAddAddOn: (parent: Service, addOn: Service['add_ons'][number]) => void;
  onPickProduct: (product: Product) => void;
}): ReactNode {
  const money = (cents: number): string => formatMoney(cents, currency, locale);
  const sellServices = useMemo(() => sellableServices(services), [services]);
  const sellProducts = useMemo(() => sellableProducts(products), [products]);
  const tabs = [
    ...(sellServices.length > 0 ? (['services'] as const) : []),
    ...(sellProducts.length > 0 ? (['products'] as const) : []),
  ];
  const [tabChoice, setTab] = useState<'services' | 'products'>('services');
  const tab = tabs.includes(tabChoice) ? tabChoice : (tabs[0] ?? 'services');
  const [query, setQuery] = useState('');
  /** The service whose lengths are open, if any. One at a time. */
  const [choosing, setChoosing] = useState<string | null>(null);

  const suggestions = addOnSuggestions(lines, services);

  /* ---- one tile per thing ---- */

  const serviceMeta = (s: Service): string => {
    const options = liveOptions(s);
    if (options.length >= 2) {
      const cheapest = Math.min(...options.map((o) => o.price_cents));
      return t('checkout.tileLengths', { count: options.length, price: money(cheapest) });
    }
    return t('checkout.tileService', { minutes: s.duration_min, price: money(s.price_cents) });
  };

  const tapService = (s: Service): void => {
    if (liveOptions(s).length >= 2) {
      setChoosing((current) => (current === s.service_id ? null : s.service_id));
      return;
    }
    onAddService(s);
    setQuery('');
  };

  const serviceTile = (s: Service): ReactNode => {
    const options = liveOptions(s);
    const open = choosing === s.service_id;
    return (
      <li key={s.service_id} className={open ? 'pos-tiles__open' : undefined}>
        <Tile
          name={s.name}
          meta={serviceMeta(s)}
          images={s.images}
          onClick={() => tapService(s)}
          {...(options.length >= 2 ? { expanded: open } : {})}
        />
        {open ? (
          <div
            className="pos-lengths"
            role="group"
            aria-label={t('checkout.chooseLength', { name: s.name })}
          >
            <p className="pos-lengths__title">{t('checkout.chooseLength', { name: s.name })}</p>
            {options.map((o) => (
              <button
                key={o.option_id}
                type="button"
                className="pos-lengths__choice"
                onClick={() => {
                  onAddService(s, o);
                  setChoosing(null);
                  setQuery('');
                }}
              >
                <span>
                  {o.duration_min === null
                    ? o.name
                    : t('checkout.minutes', { minutes: o.duration_min })}
                </span>
                <strong>{money(o.price_cents)}</strong>
              </button>
            ))}
          </div>
        ) : null}
      </li>
    );
  };

  const productTile = (p: Product): ReactNode => (
    <li key={p.product_id}>
      <Tile
        name={p.name}
        meta={p.open_price ? t('checkout.priceAtTill') : money(p.price_cents)}
        images={p.images}
        onClick={() => {
          onPickProduct(p);
          setQuery('');
        }}
      />
    </li>
  );

  /* ---- searching: both tabs at once ---- */

  const needle = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (needle === '') return [];
    const all = [
      ...sellServices.map((s) => ({ id: s.service_id, name: s.name, service: s })),
      ...sellProducts.map((p) => ({ id: p.product_id, name: p.name, product: p })),
    ].filter((item) => item.name.toLowerCase().includes(needle));
    return byRecency(all, recents);
  }, [needle, sellServices, sellProducts, recents]);

  const takeOnlyMatch = (): void => {
    const only = matches.length === 1 ? matches[0] : undefined;
    if (only === undefined) return;
    if ('service' in only && only.service !== undefined) tapService(only.service);
    else if ('product' in only && only.product !== undefined) {
      onPickProduct(only.product);
      setQuery('');
    }
  };

  return (
    <div className="pos-catalog">
      <input
        type="search"
        aria-label={t('checkout.searchLabel')}
        className="pos-catalog__search"
        placeholder={t('checkout.searchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          takeOnlyMatch();
        }}
      />

      {needle !== '' ? (
        <ul className="pos-tiles" aria-label={t('checkout.searchResults')}>
          {matches.map((item) =>
            'service' in item && item.service !== undefined
              ? serviceTile(item.service)
              : 'product' in item && item.product !== undefined
                ? productTile(item.product)
                : null,
          )}
          {matches.length === 0 ? (
            <li className="pos-catalog__empty muted">
              {t('checkout.searchNoMatch', { query: query.trim() })}
            </li>
          ) : null}
        </ul>
      ) : (
        <>
          {tabs.length > 1 ? (
            <div className="pos-tabs" role="tablist" aria-label={t('checkout.catalogLabel')}>
              {tabs.map((name) => (
                <button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={tab === name}
                  className={`pos-tabs__tab${tab === name ? ' is-current' : ''}`}
                  onClick={() => setTab(name)}
                >
                  {name === 'services' ? t('checkout.tabServices') : t('checkout.tabProducts')}
                </button>
              ))}
            </div>
          ) : null}

          {tabs.length === 0 ? (
            <p className="pos-catalog__empty muted">{t('checkout.catalogEmpty')}</p>
          ) : tab === 'services' ? (
            <div role={tabs.length > 1 ? 'tabpanel' : undefined}>
              {suggestions.map(({ parent, addOns }) => (
                <section key={parent.service_id} className="pos-group pos-group--addons">
                  <h4 className="pos-group__heading">
                    {t('checkout.addOnsFor', { name: parent.name })}
                  </h4>
                  <ul className="pos-tiles">
                    {addOns.map((a) => (
                      <li key={a.service_id}>
                        <Tile
                          name={a.name}
                          meta={t('checkout.tileAddOn', {
                            minutes: a.duration_min,
                            price: money(a.price_cents),
                          })}
                          images={undefined}
                          onClick={() => onAddAddOn(parent, a)}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
              {groupServices(sellServices).map(([heading, rows], index, all) => (
                <section key={heading ?? '—'} className="pos-group">
                  {heading === null ? (
                    all.length > 1 ? (
                      <h4 className="pos-group__heading">{t('checkout.moreServices')}</h4>
                    ) : null
                  ) : (
                    <h4 className="pos-group__heading">{heading}</h4>
                  )}
                  <ul className="pos-tiles" data-group-index={index}>
                    {rows.map(serviceTile)}
                  </ul>
                </section>
              ))}
            </div>
          ) : (
            <div role={tabs.length > 1 ? 'tabpanel' : undefined}>
              <ul className="pos-tiles">{sellProducts.map(productTile)}</ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * One tappable thing. The accessible name is "name, price" (and the length) so
 * a screen reader hears what the eye reads; the photograph is decoration.
 */
function Tile({
  name,
  meta,
  images,
  onClick,
  expanded,
}: {
  name: string;
  meta: string;
  images: readonly ItemImage[] | undefined;
  onClick: () => void;
  expanded?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      className="pos-tile"
      aria-label={`${name}, ${meta}`}
      {...(expanded === undefined ? {} : { 'aria-expanded': expanded })}
      onClick={onClick}
    >
      <ItemThumb images={images} className="pos-tile__photo" />
      <span className="pos-tile__name">{name}</span>
      <span className="pos-tile__meta">{meta}</span>
    </button>
  );
}
